import React, { useEffect, useState } from 'react';
import { Database, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { api } from '../../lib/api';

type Status = { enabled: boolean; standort: string; seeded?: boolean };
type Ergebnis = { ok: boolean; log: string[] };

interface Props {
  /** Was statt der Karte steht, wenn der Seed nicht freigeschaltet ist. */
  fallback?: React.ReactNode;
}

/**
 * Testdatenstand aus dem Office heraus anlegen — **vorübergehend**.
 *
 * Damit lässt sich eine manuelle Testsitzung jederzeit auf einen bekannten Stand
 * zurücksetzen. Die Karte zeigt sich nur, wenn das Backend den Seed freigeschaltet
 * hat (E2E_SEED_ENABLED); zum Livegang fliegt beides wieder raus.
 */
export default function E2eSeedCard({ fallback = null }: Props = {}) {
  const [status, setStatus] = useState<Status | null>(null);
  const [läuft, setLäuft] = useState<'seed' | 'reset' | null>(null);
  const [protokoll, setProtokoll] = useState<string[]>([]);

  useEffect(() => {
    let abgebrochen = false;
    api
      .get<Status>('/api/admin/e2e-seed')
      .then((antwort) => {
        if (!abgebrochen) setStatus(antwort);
      })
      .catch(() => {
        // Ohne Berechtigung oder ohne Schalter gibt es die Karte einfach nicht.
        if (!abgebrochen) setStatus({ enabled: false, standort: '' });
      });
    return () => {
      abgebrochen = true;
    };
  }, []);

  if (status === null) return null;
  if (!status.enabled) return <>{fallback}</>;

  const ausführen = async (reset: boolean) => {
    if (reset && !globalThis.confirm(
      `Den Standort „${status.standort}" löschen und neu aufbauen?\n\n`
      + 'Betrifft nur diesen Standort. Personen, die auch anderswo Anmeldungen '
      + 'haben, bleiben stehen.',
    )) {
      return;
    }
    setLäuft(reset ? 'reset' : 'seed');
    setProtokoll([]);
    try {
      const antwort = await api.post<Ergebnis>('/api/admin/e2e-seed', { reset });
      setProtokoll(antwort.log || []);
      toast.success(reset ? 'Testdaten zurückgesetzt und neu angelegt' : 'Testdaten angelegt');
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Testdaten fehlgeschlagen');
    } finally {
      setLäuft(null);
    }
  };

  return (
    <Card className="border-dashed border-amber-400/70">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Database className="h-4 w-4 text-amber-500" />
          Testdaten
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-normal text-amber-800">
            nur Testphase
          </span>
          {/* Erspart die Frage „habe ich das hier schon angelegt?“ */}
          {status.seeded !== undefined && (
            <span className="text-xs font-normal text-muted-foreground">
              {status.seeded ? 'steht bereits' : 'noch nicht angelegt'}
            </span>
          )}
        </CardTitle>
        <p className="mt-1 text-sm text-muted-foreground">
          Legt im Standort „{status.standort}" einen bekannten Stand an: Lehrkräfte mit
          Freigabezeiten, Tarife, eine Familie mit drei Lernenden und Mandat, Kurse von
          „voll" bis „frei", jede Trichterstufe und Vertragskonten mit offener,
          bezahlter, überfälliger und zurückgegebener Forderung. Dazu drei Verträge mit
          Vorgeschichte: Paket mit Verbrauch und automatischem Folgepaket, Ratenzahlung mit
          Rücklastschrift und fünf Monate bezahlter Pakete. Der echte Bestand bleibt
          unberührt.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Alle Daten hängen am Ersten des laufenden Monats: Ein Zurücksetzen erzeugt im
          selben Monat genau denselben Stand, auch die Zahlungsläufe lassen sich so
          nachvollziehen. Das Zurücksetzen räumt die Zahlungsläufe mit Testdaten mit weg.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={läuft !== null}
            onClick={() => ausführen(false)}
            className="gap-1.5"
          >
            {läuft === 'seed' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
            Anlegen bzw. ergänzen
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={läuft !== null}
            onClick={() => ausführen(true)}
            className="gap-1.5"
          >
            {läuft === 'reset' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            Zurücksetzen und neu anlegen
          </Button>
        </div>
        {protokoll.length > 0 && (
          <pre className="max-h-48 overflow-auto rounded-md bg-muted p-3 text-xs leading-relaxed whitespace-pre-wrap">
            {protokoll.join('\n')}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}
